import { workflowPayloadFromWorkflow } from "components/admin/tenant/workflow/workflow-edit";
import { DeleteButton } from "components/ui/action-button";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryButton } from "components/ui/secondary-button";
import { useDeleteWorkflowMutation, WorkflowStatus } from "generated/graphql";
import React from "react";
import { navigate } from "store/actions";
import { handleEvent } from "util/user-events";
import { EditableWorkflow } from "../types";
import { WorkflowEventProperties } from "./builder-analytics";
import { workflowPublishBlocker } from "./builder-helper";
import { WorkflowEditor } from "./use-workflow-editor";

export type WorkflowPublishSource = "header" | "review";

export interface WorkflowStatusActionsController {
    editable: boolean;
    isDraft: boolean;
    saving: boolean;
    publishBlocker: string | null;
    publish: (source: WorkflowPublishSource) => Promise<boolean>;
    unpublish: () => Promise<void>;
    remove: () => Promise<void>;
}

interface UseWorkflowStatusActionsParams {
    workflow: EditableWorkflow;
    editor: WorkflowEditor;
    eventProperties: WorkflowEventProperties;
    refetch: () => Promise<void>;
}

export const useWorkflowStatusActions = (
    params: UseWorkflowStatusActionsParams,
): WorkflowStatusActionsController => {
    const { workflow, editor, eventProperties, refetch } = params;
    const { editPayload, saving } = editor;
    const deleteWorkflowMutation = useDeleteWorkflowMutation();

    const editable = !!workflow.regionVisibility?.editable;
    const isDraft = workflow.status === WorkflowStatus.Draft;
    const publishBlocker = React.useMemo(
        () => workflowPublishBlocker(workflowPayloadFromWorkflow(workflow)),
        [workflow],
    );

    const publish = React.useCallback(
        async (source: WorkflowPublishSource) => {
            if (!editable || !isDraft || publishBlocker) {
                return false;
            }
            const confirmed = await fancyConfirm(
                "Publish workflow?",
                "This workflow will go live and run whenever its trigger is met. You can unpublish it at any time.",
                "Yes, publish",
                "Cancel",
                { confirmStyle: "question" },
            );
            if (!confirmed) {
                return false;
            }
            await editPayload(payload => ({
                ...payload,
                status: WorkflowStatus.Published,
            }));
            handleEvent("workflow-builder-publish", { ...eventProperties, source });
            return true;
        },
        [editable, isDraft, publishBlocker, editPayload, eventProperties],
    );

    const unpublish = React.useCallback(async () => {
        if (!editable || isDraft) {
            return;
        }
        const confirmed = await fancyConfirm(
            "Unpublish workflow?",
            "This workflow will stop running for everyone. Anything it has already scheduled will be cancelled and will not run. You can publish it again at any time.",
            "Yes, unpublish",
            "Cancel",
        );
        if (!confirmed) {
            return;
        }
        await editPayload(payload => ({
            ...payload,
            status: WorkflowStatus.Draft,
        }));
        handleEvent("workflow-builder-unpublish", eventProperties);
    }, [editable, isDraft, editPayload, eventProperties]);

    const remove = React.useCallback(async () => {
        if (!editable) {
            return;
        }
        await deleteWorkflowMutation.mutateAsync({ workflowId: workflow.id });
        handleEvent("workflow-builder-delete", eventProperties);
        await refetch();
        navigate("/settings/workflow");
    }, [editable, deleteWorkflowMutation, workflow.id, refetch, eventProperties]);

    return { editable, isDraft, saving, publishBlocker, publish, unpublish, remove };
};

interface WorkflowStatusActionsProps {
    actions: WorkflowStatusActionsController;
}

export const WorkflowStatusActions: React.FC<WorkflowStatusActionsProps> = props => {
    const { actions } = props;
    const { editable, isDraft, saving, publishBlocker, publish, unpublish, remove } =
        actions;

    const handlePublish = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await publish("header");
        },
        [publish],
    );

    const handleUnpublish = React.useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await unpublish();
        },
        [unpublish],
    );

    if (!editable) {
        return null;
    }

    return (
        <div className="flex items-center gap-2">
            {isDraft ? (
                <SecondaryButton
                    onClick={handlePublish}
                    disabled={saving || !!publishBlocker}
                    title={publishBlocker ?? undefined}>
                    Publish
                </SecondaryButton>
            ) : (
                <SecondaryButton onClick={handleUnpublish} disabled={saving}>
                    Unpublish
                </SecondaryButton>
            )}
            <DeleteButton
                backgroundColor="bg-white"
                confirm={{
                    title: "Delete workflow?",
                    message:
                        "Are you sure you want to delete this workflow? Any actions this workflow has already scheduled will be cancelled and will not run.",
                }}
                onClick={remove}
            />
        </div>
    );
};
